import { TypeDto, UserDto } from 'app/api/apiClient/generated/types';
import { CompanyTypeId, MissingDetail, UserRoles } from 'app/constants/user';

// eslint-disable-next-line @stylistic/max-len
const hasRole = (roles: TypeDto[], userRole: UserRoles): boolean => roles.some(role => role.id as UserRoles === userRole);

// Exports
// Payee
export function isPayee(user: null | UserDto): user is UserDto {
  return !!user && user.company?.type.id === CompanyTypeId.Payee;
}
export function isPayeeAdmin(user: null | UserDto): boolean {
  return isPayee(user) && hasRole(user.roles ?? [], UserRoles.PayeeAdmin);
}
export function isPayeeBilling(user: null | UserDto): boolean {
  return isPayee(user) && hasRole(user.roles ?? [], UserRoles.PayeeBilling);
}
export function isPayeeDriver(user: null | UserDto): boolean {
  return isPayee(user) && hasRole(user.roles ?? [], UserRoles.PayeeDriver);
}
export function isPayeeAndOnlyDriver(user: null | UserDto): boolean {
  const userIsPayeeAdmin = isPayeeAdmin(user);
  const userIsPayeeBilling = isPayeeBilling(user);
  const userIsPayeeDriver = isPayeeDriver(user);

  return userIsPayeeDriver && !userIsPayeeAdmin && !userIsPayeeBilling;
}

// Payor
export function isPayor(user: null | UserDto): user is UserDto {
  return !!user && user.company?.type.id === CompanyTypeId.Payor;
}
export function isPayorAdmin(user: null | UserDto): boolean {
  return isPayor(user) && hasRole(user.roles ?? [], UserRoles.UserAdmin);
}
export function isPayorSubmission(user: null | UserDto): boolean {
  return isPayor(user) && hasRole(user.roles ?? [], UserRoles.PayorSubmission);
}
export function isPayorBillingReports(user: null | UserDto): boolean {
  return isPayor(user) && hasRole(user.roles ?? [], UserRoles.PayorReports);
}

// Factor
export function isFactor(user: null | UserDto): user is UserDto {
  return !!user && user.company?.type.id === CompanyTypeId.Factor;
}
export function isFactorAdmin(user: null | UserDto): boolean {
  return isFactor(user) && hasRole(user.roles ?? [], UserRoles.UserAdmin);
}

// Internal
export function isTriumphInternal(authRoles: UserRoles[]): boolean {
  return authRoles.includes(UserRoles.TriumphInternal);
}
export function isInternalOpsManager(authRoles: UserRoles[]): boolean {
  return authRoles.includes(UserRoles.OpsManager);
}
export function isInternalOpsLeadership(authRoles: UserRoles[]): boolean {
  return authRoles.includes(UserRoles.OpsLeadership);
}
export function isInternalIntegrationsManager(authRoles: UserRoles[]): boolean {
  return authRoles.includes(UserRoles.IntegrationManager);
}
export function isUnderwriting(authRoles: UserRoles[]): boolean {
  return authRoles.includes(UserRoles.Underwriting);
}
export function isTreasury(authRoles: UserRoles[]): boolean {
  return authRoles.includes(UserRoles.Treasury);
}
export function isAppManager(authRoles: UserRoles[]): boolean {
  return authRoles.includes(UserRoles.AppManager);
}

export function isTriumphInternalOrAppManager(authRoles: UserRoles[]): boolean {
  return isTriumphInternal(authRoles) || isAppManager(authRoles);
}

// External & NOA
export function isExternalVendor(user: null | UserDto): boolean {
  return !!user && user.company?.type.id === CompanyTypeId.ExternalVendor;
}
export function isNoaAuditor(authRoles: UserRoles[]): boolean {
  return authRoles.some(role => role === UserRoles.NoaAuditIbex);
}
export function isNoaProcessor(authRoles: UserRoles[]): boolean {
  return authRoles.some(role => role === UserRoles.NoaIBEX);
}
export function isNoaProcessorManager(authRoles: UserRoles[]): boolean {
  return authRoles.some(role => role === UserRoles.NoaManagerIBEX);
}

export function getFirstMissingDetail(user: null | UserDto): MissingDetail | null {
  if (!user) {
    return null;
  }

  const missingDetail = (user.missingDetails ? Object.keys(user.missingDetails) : [])
    .find((detailKey: MissingDetail) => user.missingDetails?.[detailKey]);

  if (!missingDetail) {
    return null;
  }

  return missingDetail as MissingDetail;
}
